import React, { forwardRef, useMemo } from "react";
import i18n from "@/utils/i18n";
import { CertificateTemplate, TextPosition } from "../../config/certificate-templates";
import { CertificateData, formatCertificateDate } from "../../utils/certificate-pdf-generator";
import styles from "./CertificatePreview.module.scss";

interface CertificatePreviewProps {
  template: CertificateTemplate;
  data: CertificateData;
  scale?: number;
}

/** Placeholder keys for saved certificate HTML (e.g. {{educationName}}) */
export const CERTIFICATE_FIELD_KEYS = {
  educationTitle: "educationName",
  participantName: "participantName",
  description: "description",
  instructorName: "instructorName",
  issueDate: "issueDate",
  certificateNumber: "certificateNumber",
} as const;

interface TextOverlayProps {
  position: TextPosition;
  value: string;
  label?: string;
  showLabel?: boolean;
  /** Key for template placeholder when saving (e.g. educationName => {{educationName}}) */
  fieldKey?: keyof typeof CERTIFICATE_FIELD_KEYS;
}

const TextOverlay: React.FC<TextOverlayProps> = ({ position, value, label, showLabel = false, fieldKey }) => {
  if (!value && !showLabel && !fieldKey) return null;

  // Uzun eğitim adlarının ("Çok üniteli eğitim" gibi) sertifikada
  // kayıp/bozuk görünmesini önlemek için: overflow-wrap: anywhere (uzun kelimeleri
  // de kırar), hyphens, ve fitContent ile makul bir maksimum genişlik. Centered
  // metinde translateX(-50%) çoklu satırda da düzgün hizalanır çünkü maxWidth
  // ile metnin genişliği sınırlandırılıyor.
  const style: React.CSSProperties = {
    position: "absolute",
    left: `${position.x}%`,
    top: `${position.y}%`,
    transform: `translateX(${position.textAlign === "center" ? "-50%" : position.textAlign === "right" ? "-100%" : "0"})`,
    fontFamily: position.fontFamily,
    fontSize: `${position.fontSize}px`,
    fontWeight: position.fontWeight,
    color: position.color,
    textAlign: position.textAlign,
    maxWidth: position.maxWidth ? `${position.maxWidth}%` : "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    hyphens: "auto",
    lineHeight: 1.3,
  };

  const placeholderKey = fieldKey ? CERTIFICATE_FIELD_KEYS[fieldKey] : undefined;

  return (
    <div style={style} className={styles.textOverlay}>
      {showLabel && label && (
        <div className={styles.fieldLabel}>{label.toLocaleUpperCase('tr-TR')}</div>
      )}
      <div
        className={styles.fieldValue}
        {...(placeholderKey && { "data-certificate-field": placeholderKey })}
      >
        {value}
      </div>
    </div>
  );
};

// Fixed dimensions for A4 landscape certificate (in pixels at 96 DPI)
const CERTIFICATE_WIDTH = 842;
const CERTIFICATE_HEIGHT = 595;

const CertificatePreview = forwardRef<HTMLDivElement, CertificatePreviewProps>(
  ({ template, data, scale = 1 }, ref) => {
    const containerStyle: React.CSSProperties = {
      transform: `scale(${scale})`,
      transformOrigin: "top left",
    };

    // Format the date for display (stored as ISO string)
    const formattedDate = useMemo(() => {
      if (!data.issueDate) return "";
      try {
        return formatCertificateDate(data.issueDate);
      } catch {
        return data.issueDate; // Return as-is if parsing fails
      }
    }, [data.issueDate]);

    return (
      <div className={styles.previewWrapper} style={scale !== 1 ? containerStyle : undefined}>
        <div 
          ref={ref} 
          className={styles.certificateContainer}
          style={{
            width: `${CERTIFICATE_WIDTH}px`,
            height: `${CERTIFICATE_HEIGHT}px`,
          }}
        >
          {/* Background template image */}
          <img
            src={template.imagePath}
            alt={template.name}
            className={styles.templateImage}
            crossOrigin="anonymous"
          />

          {/* Text overlays */}
          <div className={styles.overlayContainer}>
            {/* Education Title - Top center with label (Eğitim Adı => {{educationName}}) */}
            <TextOverlay
              position={template.textPositions.educationTitle}
              value={data.educationTitle}
              label={i18n.t("certificateDetail.tokens.educationName")}
              showLabel={!!data.educationTitle}
              fieldKey="educationTitle"
            />
            
            {/* Participant Name - Center prominent => {{participantName}} */}
            <TextOverlay
              position={template.textPositions.participantName}
              value={data.participantName}
              fieldKey="participantName"
            />
            
            {/* Description - Below participant name => {{description}} */}
            <TextOverlay
              position={template.textPositions.description}
              value={data.description}
              fieldKey="description"
            />
            
            {/* Instructor Name - Bottom left (Eğitmen => {{instructorName}}) */}
            <TextOverlay
              position={template.textPositions.instructorName}
              value={data.instructorName}
              label={i18n.t("certificateDetail.tokens.instructorName")}
              showLabel={!!data.instructorName}
              fieldKey="instructorName"
            />
            
            {/* Issue Date - Bottom right (Tarih => {{issueDate}}) */}
            <TextOverlay
              position={template.textPositions.issueDate}
              value={formattedDate}
              label={i18n.t("certificateDetail.tokens.issueDate")}
              showLabel={!!formattedDate}
              fieldKey="issueDate"
            />
            
            {/* Certificate Number - Bottom center (Sertifika No => {{certificateNumber}}) */}
            <TextOverlay
              position={template.textPositions.certificateNumber}
              value={data.certificateNumber}
              label="Sertifika No"
              showLabel={!!data.certificateNumber}
              fieldKey="certificateNumber"
            />
          </div>
        </div>
      </div>
    );
  }
);

CertificatePreview.displayName = "CertificatePreview";

export default CertificatePreview;
